import { useEffect, useState } from 'react'
import type { TripPathPoint } from '../../lib/tripPath'
import type { TripReplayPayload } from '../../panels/TripReplay'
import type { AlertTripEnvelope, PathWindowPoint, PathWindowResponse } from './alertTypes'

/**
 * Everything the alert→trip drawer needs for one alert, in the order it can be known:
 *
 *   1. `GET /api/reports/alerts/{alert_id}/trip`  -- the alert, its driver, and the trip
 *      (if any) whose half-open `[started_ts, ended_ts)` window contains it.
 *   2a. a real trip row (`trip.trip_id`):  `/api/trips/{id}/replay` + `/api/trips/{id}/path`
 *       in parallel -- the exact two calls the Command Centre's own `loadTripReplay` makes,
 *       so the drawer and the live replay show the same geotags and the same path.
 *   2b. no trip row (Wonder Cement has no trips at all; Ultratech alerts between legs; a
 *       `geo_leg` inferred from fence crossings has no id to call):
 *       `/api/reports/alerts/path-window?vehicle_id&from&to` for ±30 min of raw trip_path
 *       around the alert, events `[]`.
 *
 * On demand only, one AbortController per alert id, no interval -- CLAUDE.md's live-data
 * rule applies to reports too: a historical alert does not change under the reader.
 * Failures in step 2 degrade (null replay / empty path -- the header still has the
 * envelope) rather than failing the whole drawer; only step 1 is fatal.
 */

export type AlertTripStatus = 'idle' | 'loading' | 'ok' | 'error'

export interface AlertTripData {
  trip: AlertTripEnvelope
  replay: TripReplayPayload | null
  path: TripPathPoint[]
}

export interface AlertTripResult {
  status: AlertTripStatus
  error: string | null
  data: AlertTripData | null
}

/** Half-width of the no-trip path window. 30 min each side: long enough to see where
 *  the vehicle came from and went to, well inside the endpoint's 6 h span cap. */
export const PATH_WINDOW_HALF_MS = 30 * 60 * 1000

export function alertTripUrl(alertId: string): string {
  return `/api/reports/alerts/${encodeURIComponent(alertId)}/trip`
}

/** `from`/`to` go on the wire as ISO-8601 UTC -- the alert-report router parses every
 *  `from`/`to` query parameter that way (naive strings would be read as IST; a `Z` suffix
 *  removes the ambiguity). The response echoes them back as epoch ms like every `*_ts`. */
export function pathWindowUrl(vehicleId: string, aroundTs: number, halfMs = PATH_WINDOW_HALF_MS): string {
  const q = new URLSearchParams({
    vehicle_id: vehicleId,
    from: new Date(aroundTs - halfMs).toISOString(),
    to: new Date(aroundTs + halfMs).toISOString(),
    max_points: '2000',
  })
  return `/api/reports/alerts/path-window?${q.toString()}`
}

/** path-window points have no `seq` (they are not a trip's own rows); `lib/tripPath.ts`'s
 *  helpers only need the array ordered by ts, so the index serves. */
export function pathWindowToTripPath(points: readonly PathWindowPoint[]): TripPathPoint[] {
  return points
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon) && Number.isFinite(p.ts))
    .map((p, seq) => ({
      seq, ts: p.ts, lat: p.lat, lon: p.lon,
      speed_kph: p.speed_kph ?? null, heading_deg: p.heading_deg ?? null, soc_pct: p.soc_pct ?? null,
    }))
}

/** A 502/503 from the proxy is an HTML page, not JSON -- surface the status rather than
 *  a JSON parse error that says nothing about what went wrong. */
async function readJson<T>(res: Response): Promise<Partial<T> & { error?: string }> {
  try {
    return (await res.json()) as Partial<T> & { error?: string }
  } catch {
    return {}
  }
}

export async function fetchAlertTrip(alertId: string, signal?: AbortSignal): Promise<AlertTripData> {
  const res = await fetch(alertTripUrl(alertId), { signal })
  const body = await readJson<AlertTripEnvelope>(res)
  if (!res.ok || body.error || !body.alert) {
    throw new Error(body.error ?? (res.ok ? 'malformed trip response' : `HTTP ${res.status}`))
  }
  const envelope = body as AlertTripEnvelope

  const tripId = envelope.trip?.trip_id ?? null
  if (tripId) {
    const [replayRes, pathRes] = await Promise.all([
      fetch(`/api/trips/${encodeURIComponent(tripId)}/replay`, { signal }),
      fetch(`/api/trips/${encodeURIComponent(tripId)}/path`, { signal }),
    ])
    const replayBody = await readJson<TripReplayPayload>(replayRes)
    const pathBody = await readJson<{ points: TripPathPoint[] }>(pathRes)
    return {
      trip: envelope,
      replay: replayRes.ok && !replayBody.error && typeof replayBody.started_ts === 'number'
        ? (replayBody as TripReplayPayload)
        : null,
      path: pathRes.ok && !pathBody.error ? (pathBody.points ?? []) : [],
    }
  }

  // No trip row to call: a geo-inferred leg still names the vehicle; a bare alert names
  // it itself (null only for cell-based congestion alerts, which have no path to draw).
  const vehicleId = envelope.trip?.vehicle_id ?? envelope.alert.vehicle_id
  if (!vehicleId) return { trip: envelope, replay: null, path: [] }
  const pwRes = await fetch(pathWindowUrl(vehicleId, envelope.alert.ts), { signal })
  const pwBody = await readJson<PathWindowResponse>(pwRes)
  return {
    trip: envelope,
    replay: null,
    path: pwRes.ok && !pwBody.error ? pathWindowToTripPath(pwBody.points ?? []) : [],
  }
}

const IDLE: AlertTripResult = { status: 'idle', error: null, data: null }
const LOADING: AlertTripResult = { status: 'loading', error: null, data: null }

export function useAlertTrip(alertId: string | null): AlertTripResult {
  const [state, setState] = useState<AlertTripResult>(IDLE)

  useEffect(() => {
    if (!alertId) {
      setState(IDLE)
      return
    }
    const ctl = new AbortController()
    // `cancelled` rather than sniffing AbortError: readJson swallows a mid-body abort
    // into `{}`, which would otherwise surface as a bogus "malformed" error for an alert
    // the reader has already moved off.
    let cancelled = false
    setState(LOADING)
    fetchAlertTrip(alertId, ctl.signal).then(
      (data) => { if (!cancelled) setState({ status: 'ok', error: null, data }) },
      (err: unknown) => {
        if (cancelled) return
        setState({ status: 'error', error: err instanceof Error ? err.message : String(err), data: null })
      },
    )
    return () => {
      cancelled = true
      ctl.abort()
    }
  }, [alertId])

  return state
}
